import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Search, 
  ChevronLeft,
  ChevronRight,
  Trophy,
  Medal,
  Award,
  Users,
  Loader2,
  Building2,
  Mail,
  Phone,
  Edit2,
  Check,
  X,
  Clock,
  Trash2,
  Plus
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLES = {
  Gold: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-200" },
  Silver: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200" },
  Bronze: { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-200" },
  Unranked: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-100" }
};

const TierIcon = ({ tier, className = "h-4 w-4" }) => {
  switch (tier) {
    case "Gold": return <Trophy className={`${className} text-yellow-500`} />;
    case "Silver": return <Medal className={`${className} text-slate-400`} />;
    case "Bronze": return <Award className={`${className} text-orange-500`} />;
    default: return <Users className={`${className} text-slate-400`} />;
  }
};

const TierBadge = ({ tier, onClick, editable = false }) => {
  const style = TIER_STYLES[tier] || TIER_STYLES.Unranked;
  return (
    <Badge 
      className={cn(
        "tier-badge gap-1", 
        style.bg, 
        style.text, 
        "border", 
        style.border,
        editable && "cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-blue-300"
      )}
      onClick={onClick}
    >
      <TierIcon tier={tier} className="h-3 w-3" />
      {tier}
      {editable && <Edit2 className="h-3 w-3 ml-1 opacity-50" />}
    </Badge>
  );
};

const InlineTierEditor = ({ lead, onSave, onCancel }) => {
  const [selectedTier, setSelectedTier] = useState(lead.company_tier);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (selectedTier === lead.company_tier) {
      onCancel();
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/leads/${lead.id}`, { company_tier: selectedTier });
      toast.success(`Updated ${lead.company} to ${selectedTier} tier`);
      onSave(selectedTier);
    } catch (error) {
      toast.error("Failed to update tier");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <Select value={selectedTier} onValueChange={setSelectedTier}>
        <SelectTrigger className="h-7 w-[100px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Gold">Gold</SelectItem>
          <SelectItem value="Silver">Silver</SelectItem>
          <SelectItem value="Bronze">Bronze</SelectItem>
          <SelectItem value="Unranked">Unranked</SelectItem>
        </SelectContent>
      </Select>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleSave} disabled={saving}>
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 text-green-600" />}
      </Button>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onCancel}>
        <X className="h-3 w-3 text-red-500" />
      </Button>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-slate-400 text-sm">—</span>;
  
  const getStatusStyle = (s) => {
    const lower = s.toLowerCase();
    if (lower.includes("responded") || lower.includes("completed")) 
      return "bg-green-100 text-green-700 border-green-200";
    if (lower.includes("contacted") || lower.includes("sent")) 
      return "bg-blue-100 text-blue-700 border-blue-200";
    if (lower.includes("no response") || lower.includes("no needs")) 
      return "bg-slate-100 text-slate-600 border-slate-200";
    return "bg-slate-50 text-slate-500 border-slate-100";
  };
  
  return (
    <Badge variant="outline" className={cn("text-xs font-normal", getStatusStyle(status))}>
      {status.length > 25 ? status.slice(0, 25) + "..." : status}
    </Badge>
  );
};

export default function Leads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [editingTierId, setEditingTierId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [newLead, setNewLead] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    status: "",
    company_tier: "Unranked",
    notes: ""
  });
  const [addingLead, setAddingLead] = useState(false);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [tier, setTier] = useState(searchParams.get("tier") || "all");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [therapeuticArea, setTherapeuticArea] = useState(searchParams.get("therapeutic_area") || "all");
  const [modifiedOnly, setModifiedOnly] = useState(false);
  const [taOptions, setTaOptions] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const limit = 50;

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tier && tier !== "all") params.append("tier", tier);
      if (status && status !== "all") params.append("status", status);
      if (search) params.append("search", search);
      if (modifiedOnly) params.append("modified_only", "true");
      if (therapeuticArea && therapeuticArea !== "all") params.append("therapeutic_area", therapeuticArea);
      params.append("limit", limit.toString());
      params.append("skip", ((page - 1) * limit).toString());

      const [leadsRes, countRes] = await Promise.all([
        axios.get(`${API}/leads?${params.toString()}`),
        axios.get(`${API}/leads/count?${params.toString()}`)
      ]);
      
      setLeads(leadsRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      console.error("Fetch leads error:", error);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [tier, status, search, page, modifiedOnly, therapeuticArea]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    axios.get(`${API}/dashboard/therapeutic-areas-list`).then(res => {
      setTaOptions(res.data.therapeutic_areas || []);
    }).catch(() => {});
  }, []);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (tier && tier !== "all") params.set("tier", tier);
    if (status && status !== "all") params.set("status", status);
    if (search) params.set("search", search);
    setSearchParams(params);
  }, [tier, status, search, setSearchParams]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleTierChange = (value) => {
    setTier(value);
    setPage(1);
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    setPage(1);
  };

  const handleTierUpdate = (leadId, newTier) => {
    setLeads(prev => prev.map(l => 
      l.id === leadId ? { ...l, company_tier: newTier } : l
    ));
    setEditingTierId(null);
  };

  const deleteLead = async (leadId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success("Lead deleted");
      setDeleteConfirm(null);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const createLead = async () => {
    if (!newLead.first_name && !newLead.last_name && !newLead.email) {
      toast.error("Please provide at least a name or email");
      return;
    }
    
    setAddingLead(true);
    try {
      await axios.post(`${API}/leads`, newLead);
      toast.success("Lead created successfully");
      setShowAddDialog(false);
      setNewLead({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        company: "",
        role: "",
        status: "",
        company_tier: "Unranked",
        notes: ""
      });
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create lead");
    } finally {
      setAddingLead(false);
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  const searchCompanies = async (query) => {
    if (!query || query.length < 2) {
      setCompanyOptions([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/companies/names?search=${encodeURIComponent(query)}`);
      setCompanyOptions(response.data.companies || []);
      setShowCompanyDropdown(true);
    } catch (error) {
      console.error("Error fetching companies", error);
    }
  };

  const getTherapeuticAreas = (lead) => {
    const areas = [];
    if (lead.dermatology > 0) areas.push("Derm");
    if (lead.rheumatology > 0) areas.push("Rheum");
    if (lead.neurology > 0) areas.push("Neuro");
    if (lead.ophthalmology > 0) areas.push("Ophthal");
    if (lead.immunology > 0) areas.push("Immuno");
    if (lead.pulmonary > 0) areas.push("Pulm");
    if (lead.cardiology > 0) areas.push("Cardio");
    if (lead.onco > 0) areas.push("Onco");
    return areas.slice(0, 3);
  };

  const isRecentlyModified = (lead) => {
    if (!lead.last_updated) return false;
    const lastUpdate = new Date(lead.last_updated);
    const dayAgo = new Date();
    dayAgo.setDate(dayAgo.getDate() - 7);
    return lastUpdate > dayAgo;
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1C2A25] font-['Outfit'] tracking-tight">Lead Pipeline</h1>
          <p className="text-slate-500 mt-1">
            {totalCount.toLocaleString()} leads {tier !== "all" && `in ${tier} tier`}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:flex-wrap gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by company, contact, or email..."
                value={search}
                onChange={handleSearch}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Tier Filter */}
            <Select value={tier} onValueChange={handleTierChange}>
              <SelectTrigger className="w-full lg:w-[180px]" data-testid="tier-filter">
                <SelectValue placeholder="All Tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="Gold">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-yellow-500" />
                    Gold
                  </div>
                </SelectItem>
                <SelectItem value="Silver">
                  <div className="flex items-center gap-2">
                    <Medal className="h-4 w-4 text-slate-400" />
                    Silver
                  </div>
                </SelectItem>
                <SelectItem value="Bronze">
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4 text-orange-500" />
                    Bronze
                  </div>
                </SelectItem>
                <SelectItem value="Unranked">Unranked</SelectItem>
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-full lg:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="No Response">No Response</SelectItem>
                <SelectItem value="Contacted">Contacted</SelectItem>
                <SelectItem value="Responded">Responded</SelectItem>
                <SelectItem value="Responded: No Needs Currently">No Needs</SelectItem>
              </SelectContent>
            </Select>

            {/* TA Filter */}
            <Select value={therapeuticArea} onValueChange={(v) => { setTherapeuticArea(v); setPage(1); }}>
              <SelectTrigger className="w-full lg:w-[200px]" data-testid="ta-filter">
                <SelectValue placeholder="All TAs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Therapeutic Areas</SelectItem>
                {taOptions.map(ta => (
                  <SelectItem key={ta} value={ta}>{ta}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Modified Only Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-slate-50">
              <Checkbox 
                id="modified" 
                checked={modifiedOnly}
                onCheckedChange={(checked) => {
                  setModifiedOnly(checked);
                  setPage(1);
                }}
              />
              <label htmlFor="modified" className="text-sm text-slate-600 cursor-pointer flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Modified Recently
              </label>
            </div>

            {/* Add Lead Button */}
            <Button onClick={() => setShowAddDialog(true)} className="bg-[#2E7AD1] hover:bg-[#2563EB]">
              <Plus className="h-4 w-4 mr-2" />
              Add Lead
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-[#2E7AD1]" />
            </div>
          ) : leads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Users className="h-12 w-12 mb-4 text-slate-300" />
              <p className="text-lg font-medium">No leads found</p>
              <p className="text-sm">Try adjusting your filters or search term</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="w-[100px]">Tier</TableHead>
                    <TableHead className="min-w-[200px]">Company</TableHead>
                    <TableHead className="min-w-[180px]">Contact</TableHead>
                    <TableHead className="min-w-[200px]">Email</TableHead>
                    <TableHead className="min-w-[120px]">Status</TableHead>
                    <TableHead className="min-w-[140px]">Therapeutic Areas</TableHead>
                    <TableHead className="w-[80px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead, idx) => (
                    <TableRow 
                      key={lead.id} 
                      className={cn(
                        "lead-row",
                        isRecentlyModified(lead) && "bg-blue-50/30"
                      )}
                      style={{ animationDelay: `${idx * 0.02}s` }}
                    >
                      <TableCell>
                        {editingTierId === lead.id ? (
                          <InlineTierEditor 
                            lead={lead}
                            onSave={(newTier) => handleTierUpdate(lead.id, newTier)}
                            onCancel={() => setEditingTierId(null)}
                          />
                        ) : (
                          <TierBadge 
                            tier={lead.company_tier} 
                            editable
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditingTierId(lead.id);
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          <div>
                            <Link 
                              to={`/companies/${encodeURIComponent(lead.company || '')}`}
                              className="font-medium text-[#2D5A4C] hover:text-[#23473C] hover:underline truncate max-w-[180px] block"
                              data-testid={`lead-company-link-${lead.id}`}
                            >
                              {lead.company || "—"}
                            </Link>
                            {lead.role && (
                              <p className="text-xs text-slate-500 truncate max-w-[180px]">
                                {lead.role}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <Link 
                            to={`/leads/${lead.id}`}
                            className="font-medium text-[#2D5A4C] hover:text-[#23473C] hover:underline"
                            data-testid={`lead-name-link-${lead.id}`}
                          >
                            {lead.first_name || lead.contact || "—"} {lead.last_name || ""}
                          </Link>
                          {lead.phone && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {lead.phone}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {lead.email ? (
                          <a 
                            href={`mailto:${lead.email}`}
                            className="text-[#2E7AD1] hover:underline text-sm flex items-center gap-1"
                          >
                            <Mail className="h-3 w-3" />
                            <span className="truncate max-w-[180px]">{lead.email}</span>
                          </a>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={lead.status} />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {getTherapeuticAreas(lead).map((ta) => (
                            <Badge 
                              key={ta} 
                              variant="outline" 
                              className="text-xs bg-slate-50 text-slate-600 border-slate-200"
                            >
                              {ta}
                            </Badge>
                          ))}
                          {getTherapeuticAreas(lead).length === 0 && (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteConfirm(lead)}
                            data-testid={`delete-lead-${lead.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t">
            <p className="text-sm text-slate-500">
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount.toLocaleString()}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600 px-2">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="next-page-btn"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Lead</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deleteConfirm?.first_name || deleteConfirm?.contact || 'this lead'} 
              {deleteConfirm?.company && ` from ${deleteConfirm.company}`}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => deleteLead(deleteConfirm?.id)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
            <DialogDescription>
              Manually add a new lead to your pipeline
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">First Name</label>
                <Input
                  placeholder="John"
                  value={newLead.first_name}
                  onChange={(e) => setNewLead(l => ({ ...l, first_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Last Name</label>
                <Input
                  placeholder="Doe"
                  value={newLead.last_name}
                  onChange={(e) => setNewLead(l => ({ ...l, last_name: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input
                  type="email"
                  placeholder="john.doe@company.com"
                  value={newLead.email}
                  onChange={(e) => setNewLead(l => ({ ...l, email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone</label>
                <Input
                  placeholder="+1 (555) 123-4567"
                  value={newLead.phone}
                  onChange={(e) => setNewLead(l => ({ ...l, phone: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 relative">
                <label className="text-sm font-medium">Company</label>
                <Input
                  placeholder="Start typing company name..."
                  value={newLead.company}
                  onChange={(e) => {
                    setNewLead(l => ({ ...l, company: e.target.value }));
                    searchCompanies(e.target.value);
                  }}
                  onFocus={() => newLead.company?.length >= 2 && setShowCompanyDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
                />
                {showCompanyDropdown && companyOptions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                    {companyOptions.slice(0, 10).map((company) => (
                      <div
                        key={company}
                        className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                        onMouseDown={() => {
                          setNewLead(l => ({ ...l, company }));
                          setShowCompanyDropdown(false);
                        }}
                      >
                        {company}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Role / Title</label>
                <Input
                  placeholder="VP Clinical Operations"
                  value={newLead.role}
                  onChange={(e) => setNewLead(l => ({ ...l, role: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <Select 
                  value={newLead.status} 
                  onValueChange={(v) => setNewLead(l => ({ ...l, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New">New</SelectItem>
                    <SelectItem value="Contacted">Contacted</SelectItem>
                    <SelectItem value="No Response">No Response</SelectItem>
                    <SelectItem value="Responded">Responded</SelectItem>
                    <SelectItem value="Responded: No Needs Currently">No Needs Currently</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Tier</label>
                <Select 
                  value={newLead.company_tier} 
                  onValueChange={(v) => setNewLead(l => ({ ...l, company_tier: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Gold">
                      <div className="flex items-center gap-2">
                        <Trophy className="h-4 w-4 text-yellow-500" />
                        Gold
                      </div>
                    </SelectItem>
                    <SelectItem value="Silver">
                      <div className="flex items-center gap-2">
                        <Medal className="h-4 w-4 text-slate-400" />
                        Silver
                      </div>
                    </SelectItem>
                    <SelectItem value="Bronze">
                      <div className="flex items-center gap-2">
                        <Award className="h-4 w-4 text-orange-500" />
                        Bronze
                      </div>
                    </SelectItem>
                    <SelectItem value="Unranked">Unranked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                placeholder="Additional notes about this lead..."
                value={newLead.notes}
                onChange={(e) => setNewLead(l => ({ ...l, notes: e.target.value }))}
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createLead} disabled={addingLead} className="bg-[#2E7AD1] hover:bg-[#2563EB]">
              {addingLead ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
